import { useCallback, useMemo } from 'react'

import { clsx } from 'clsx'

import { SvgIcon } from '../Icon/SvgIcon.js'
import { useUniqueId } from '../utils/unique-id.js'

import { TimePicker } from './TimePicker.js'
import { DateTimeSeparateInputPickerProps, DateValue } from './types.js'
import { useDatePicker } from './useDatePicker.js'
import { cleanDate, getFormattedTime, isSameDay, startOfDay } from './utils.js'

export function DateTimeSeparateInputPicker({
  className,
  dateFormat = 'Y-m-d',
  onChange,
  value,
  disabled,
  invalid,
  canClear,
  minDate,
}: DateTimeSeparateInputPickerProps) {
  const datePrefix = useUniqueId('DatePicker')

  const timeValue = useMemo(() => {
    return getFormattedTime(value) || ''
  }, [value])

  const dateValue = useMemo(() => {
    return value ? cleanDate(value) : ''
  }, [value])

  const minDateValue = useMemo(() => {
    if (!minDate) {
      return undefined
    }
    return cleanDate(startOfDay(minDate))
  }, [minDate])

  const minTimeValue = useMemo(() => {
    if (!minDate || !isSameDay(minDate, value)) {
      return undefined
    }
    return getFormattedTime(minDate)
  }, [minDate, value])

  const applyDateTimeRules = (date: DateValue, dateTime: DateValue) => {
    const newDate = new Date(date)
    const time = new Date(dateTime)
    newDate.setHours(time.getHours())
    newDate.setMinutes(time.getMinutes())
    newDate.setMilliseconds(0)
    newDate.setSeconds(0)

    // If selected value is before min date, select min date as value
    if (minDate && newDate.getTime() < new Date(minDate).getTime()) {
      return new Date(minDate)
    }
    return newDate
  }

  const handleDateOnChange = (dates: DateValue[]) => {
    if (!Array.isArray(dates) || dates.length !== 1) {
      if (canClear) {
        handleClear()
      } else if (dateInputField.current?._flatpickr) {
        // Reset to previous valid value
        dateInputField.current._flatpickr.setDate(dateValue)
      }
      return
    }
    const date = dates[0]
    onChange?.(applyDateTimeRules(date, value))
  }

  const { startInputField: dateInputField } = useDatePicker({
    closeOnSelect: true,
    value: dateValue,
    // use onClose instead of onChange, since onChange does not work with allowInput
    onClose: handleDateOnChange,
    mode: 'single',
    prefix: datePrefix,
    allowInput: true,
    noCalendar: false,
    enableTime: false,
    dateFormat,
    showMonths: 1,
    minDate: minDateValue,
  })
  // In case of clearing time or date, both values should be cleared since they belong to one single field,
  // if we only clear one, the field in the form would have the wrong value.
  const handleClear = useCallback(() => {
    if (dateInputField.current) {
      dateInputField.current.value = ''
    }
    onChange?.(undefined)
  }, [dateInputField, onChange])

  const handleTimeOnChange = useCallback(
    (time: string) => {
      if (!time) {
        if (canClear) {
          handleClear()
        }
        return
      }

      // Parse the time string (HH:mm format)
      const [hours, minutes] = time.split(':').map(Number)

      // If we have an existing date value, update its time
      // Otherwise create a new date with today's date
      const baseDate = value ? new Date(value) : new Date()
      baseDate.setHours(hours)
      baseDate.setMinutes(minutes)
      baseDate.setSeconds(0)
      baseDate.setMilliseconds(0)

      // Apply min date rules if needed
      if (minDate && baseDate.getTime() < new Date(minDate).getTime()) {
        onChange?.(new Date(minDate))
      } else {
        onChange?.(baseDate)
      }
    },
    [canClear, handleClear, minDate, onChange, value],
  )

  const moveCursorToEnd = (input: HTMLInputElement) => {
    // Set cursor to the end if it is not already focused
    if (input && document.activeElement !== input) {
      const valueLength = input.value.length
      input.focus()
      input.setSelectionRange(valueLength, valueLength)
    }
  }

  const openDatePicker = () => {
    const input = dateInputField.current?._flatpickr?.input
    moveCursorToEnd(input)
    dateInputField.current?._flatpickr?.open()
  }
  return (
    <div
      className={clsx(
        className,
        'flex items-stretch justify-between gap-y-1 border min-h-[48px] text-label-md rounded-input',
        invalid &&
          'focus-within:ring-action-destructive-subdued-pressed border-line-negative bg-surface-negative-subdued',
        !invalid &&
          'focus-has-[.active]:ring-action-primary has-[.active]:border-action-primary focus:outline-none focus-within:ring-1 ring-offset-0 has-[.active]:ring-1',
      )}
    >
      <div
        className="min-w-[90px] grow flex items-center gap-x-1 cursor-pointer h-fill pl-3 has-[.active]:bg-background-pressed hover:bg-action-neutral-hovered rounded-s-input"
        onClick={openDatePicker}
      >
        <SvgIcon size="md" name="calendar" />
        <input
          ref={dateInputField}
          className={clsx(
            'appearance-none',
            'focus-visible:outline-none bg-transparent truncate min-w-0',
          )}
          type="text"
          disabled={disabled}
        />
      </div>
      <div className="flex items-center gap-x-1 w-[75px] cursor-pointer pl-1 has-[.active]:bg-background-pressed hover:bg-action-neutral-hovered rounded-e-input">
        <TimePicker
          value={timeValue}
          onChange={handleTimeOnChange}
          minTime={minTimeValue}
        />
      </div>
    </div>
  )
}
